import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bug, Lightbulb, Sparkles, ClipboardList, CheckCircle2, XCircle, MessageSquarePlus, Filter } from "lucide-react";
import { fmtDate } from "@/lib/format";

const TYPE_ICON = { bug: Bug, suggestion: Lightbulb, improvement: Sparkles };
const TYPE_TONE = {
  bug: "text-red-300 bg-red-500/10 border-red-500/40",
  suggestion: "text-amber-300 bg-amber-500/10 border-amber-500/40",
  improvement: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40",
};
const STATUS_TONE = {
  open: "text-gold border-gold/40 bg-gold/10",
  resolved: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  dismissed: "text-zinc-500 border-white/10 bg-white/5",
};

/**
 * Changelog + Feedback inbox — one page with two tabs. The Changelog is
 * auto-generated from /app/memory/PRD.md by the backend, so any iteration
 * documented there shows up here without extra work. Admin + super_admin.
 */
export default function Changelog() {
  const { user } = useAuth();
  const canSeeFeedback = user.role === "super_admin";
  const [tab, setTab] = useState("changelog");
  const [log, setLog] = useState({ version: "", entries: [] });
  const [feedback, setFeedback] = useState([]);
  const [filter, setFilter] = useState("open");

  const reload = async () => {
    try { setLog((await api.get("/changelog")).data); } catch { /* silent */ }
    if (canSeeFeedback) {
      try { setFeedback((await api.get("/feedback")).data); } catch { /* silent */ }
    }
  };
  useEffect(() => { reload(); }, [canSeeFeedback]);

  const shown = feedback.filter((f) => filter === "all" ? true : f.status === filter);

  const updateStatus = async (id, status) => {
    await api.patch(`/feedback/${id}`, { status });
    reload();
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="changelog-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Platform</div>
        <div className="mt-1 flex items-baseline justify-between gap-4 flex-wrap">
          <h1 className="font-display text-4xl tracking-tight font-light">{canSeeFeedback ? "Release notes & Feedback" : "Release notes"}</h1>
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Current build <span className="text-gold" data-testid="changelog-version">{log.version || "—"}</span>
            {log.latest?.date && <span className="ml-2 text-zinc-600">{fmtDate(log.latest.date)}</span>}
          </div>
        </div>
        <p className="mt-1 text-zinc-400 text-sm">
          Every StageCall update in plain English. The most recent changes are at the top.
        </p>
      </header>

      <div className="flex flex-wrap gap-1 border border-white/10 rounded-md bg-[#121212] p-1 w-fit">
        <button data-testid="changelog-tab-log" onClick={() => setTab("changelog")} className={`h-9 px-3 rounded text-sm inline-flex items-center gap-1.5 transition-colors ${tab === "changelog" ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}>
          <ClipboardList className="w-3.5 h-3.5" /> Release notes
        </button>
        {canSeeFeedback && (
          <button data-testid="changelog-tab-feedback" onClick={() => setTab("feedback")} className={`h-9 px-3 rounded text-sm inline-flex items-center gap-1.5 transition-colors ${tab === "feedback" ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}>
            <MessageSquarePlus className="w-3.5 h-3.5" /> Feedback inbox
            {feedback.filter((f) => f.status === "open").length > 0 && (
              <span className="ml-1 min-w-[18px] h-4 px-1 rounded-full bg-gold text-black text-[10px] font-semibold grid place-items-center">{feedback.filter((f) => f.status === "open").length}</span>
            )}
          </button>
        )}
      </div>

      {tab === "changelog" && (
        <div className="space-y-4">
          {log.entries?.length === 0 && (
            <div className="rounded-lg border border-dashed border-white/10 p-8 text-center text-sm text-zinc-500">
              No release notes yet — they&apos;ll appear here as new updates ship.
            </div>
          )}
          {log.entries?.map((e) => (
            <div key={e.iter} data-testid={`changelog-iter-${e.iter}`} className="rounded-lg border border-white/10 bg-[#121212] p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gold">Iteration {e.iter}</div>
                  <h3 className="mt-1 font-display text-xl leading-tight">{e.title}</h3>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{e.date || ""}</div>
              </div>
              <ul className="mt-3 space-y-2 text-sm text-zinc-300">
                {e.changes?.map((c, i) => (
                  <li key={i} className="pl-4 relative before:absolute before:left-0 before:top-2 before:w-1 before:h-1 before:rounded-full before:bg-gold/60">
                    <span dangerouslySetInnerHTML={{ __html: c.replace(/\*\*(.+?)\*\*/g, "<strong class='text-zinc-100'>$1</strong>").replace(/`([^`]+)`/g, "<code class='text-gold text-xs bg-gold/10 px-1 rounded'>$1</code>") }} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {tab === "feedback" && canSeeFeedback && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-zinc-500" />
            {["open", "resolved", "dismissed", "all"].map((s) => (
              <button key={s} data-testid={`feedback-filter-${s}`} onClick={() => setFilter(s)} className={`h-8 px-3 rounded text-xs uppercase tracking-widest border ${filter === s ? "border-gold/40 text-gold bg-gold/10" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{s}</button>
            ))}
          </div>
          {shown.length === 0 && (
            <div className="rounded-lg border border-dashed border-white/10 p-8 text-center text-sm text-zinc-500">
              No {filter === "all" ? "" : filter + " "}feedback yet.
            </div>
          )}
          {shown.map((f) => {
            const Icon = TYPE_ICON[f.type] || Bug;
            return (
              <div key={f.id} data-testid={`feedback-row-${f.id}`} className="rounded-lg border border-white/10 bg-[#121212] p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-md grid place-items-center border shrink-0 ${TYPE_TONE[f.type] || TYPE_TONE.bug}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg leading-tight">{f.title}</div>
                      <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${STATUS_TONE[f.status] || STATUS_TONE.open}`}>{f.status}</span>
                    </div>
                    <div className="text-sm text-zinc-400 mt-2 whitespace-pre-wrap">{f.description}</div>
                    <div className="text-[11px] text-zinc-500 mt-2">
                      {f.user_name} <span className="text-zinc-600">·</span> {f.user_role} <span className="text-zinc-600">·</span> {fmtDate(f.created_at)}
                      {f.page && <> <span className="text-zinc-600">·</span> <code className="text-zinc-400">{f.page}</code></>}
                    </div>
                  </div>
                  {f.status !== "resolved" && (
                    <button data-testid={`feedback-resolve-${f.id}`} onClick={() => updateStatus(f.id, "resolved")} title="Mark resolved" className="w-8 h-8 grid place-items-center rounded border border-white/10 text-emerald-300 hover:border-emerald-500/40 hover:bg-emerald-500/10">
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  )}
                  {f.status !== "dismissed" && (
                    <button data-testid={`feedback-dismiss-${f.id}`} onClick={() => updateStatus(f.id, "dismissed")} title="Dismiss" className="w-8 h-8 grid place-items-center rounded border border-white/10 text-zinc-500 hover:border-white/25 hover:bg-white/5">
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
